/* eslint-disable sort-keys-fix/sort-keys-fix */
import { useEffect, useState } from 'react'

import { AxiosResponse } from 'axios'
import { Button, Row, Span } from 'components'
import { ExcelIcon, PdfIcon } from 'components/Svg'
import { colorsStatus } from 'components/Icons/StatusIcon/StatusIcon'
import { useAuth, useLoading, useModal, useReportPrint, useToggle } from 'hooks'
import {
  EffectiveTableData,
  EfetivoLogicoServices,
  ResGetAllContracts,
  EffectiveData,
  ReqUpdateStatusEffective,
  updateEffectiveResponse,
  ReqEffectiveByContract
} from 'services/Supplier/LogicalEffective'
import { COLORS, FORMAT, SPACING } from 'utils'

import { changeTabProps } from '../../../LogicalEffectiveRegister'
import EditEffectiveModal from '../EditEffectiveModal/EditEffectiveModal'
import { TableRegisterEffectives } from '../TableRegisterEffectives/TableRegisterEffectives'

interface CollapseProps {
  contractData: ResGetAllContracts
  handleChangeTab: (props: changeTabProps) => void
  pesquisaGeral: string
}

export const CollapseContent = ({
  contractData,
  handleChangeTab,
  pesquisaGeral
}: CollapseProps) => {
  const { setLoading } = useLoading()
  const { user } = useAuth()
  const { handlePrintEfetivoLogico } = useReportPrint()
  const { showModal } = useModal()
  const [pageNumber, setPageNumber] = useState<number>(1)
  const [rowsPerPage, setRowsPerPage] = useState(15)
  const [pageCount, setPageCount] = useState(0)
  const [selectedEffective, setSelectedEffective] = useState<
    EffectiveData | undefined
  >()
  const [bloquear, setBloquear] = useState<boolean>(false)
  const [desbloquear, setDesbloquear] = useState<boolean>(false)
  const [updateStatusObject, setUpdateStatusObject] =
    useState<ReqUpdateStatusEffective>()
  const [openEditEffectiveModal, toggleEditEffectiveModal] = useToggle()
  const [effectiveList, setEffectiveList] = useState<EffectiveTableData[]>([])
  const selectedContract = contractData.cdContrato
  const selectedNmEmpresa = contractData.nmFornecedor

  const getEfetivosPorContrato = async () => {
    setLoading('loading')

    const requestObj = {
      cdContrato: selectedContract,
      idUsuario: user.IdUsuario,
      pageNumber,
      pageSize: rowsPerPage,
      pesquisaGeral: FORMAT.unMask(pesquisaGeral)
    }

    try {
      const response = await EfetivoLogicoServices().getEfetivosporContrato(
        requestObj
      )

      if (response.status !== 200) {
        throw new Error(
          'Página em construção, não há necessidade de lançamentos.'
        )
      }

      setEffectiveList(response.data.items)
      setPageCount(response.data.pageCount)
      setLoading('idle')
    } catch (error: any) {
      setLoading('idle')
      showModal({
        hideButtonCancel: true,
        message:
          error.mensagem ||
          error.message ||
          'Página em construção, não há necessidade de lançamentos.',
        txtButtonConfirm: 'Fechar',
        type: 'error'
      })
    }
  }

  const getEffectiveData = async (idEfetivoLogico: number) => {
    setLoading('loading')

    try {
      const response = await EfetivoLogicoServices().getEfetivoLogicoById(
        idEfetivoLogico
      )

      if (response.status !== 200) {
        throw new Error('Não foi possivel obter dados do Efetivo')
      }

      setSelectedEffective(response.data)
      setLoading('idle')
      showModal({
        titleModal: 'Atenção',
        message: 'Você deseja alterar este cadastro?',
        type: 'alert',
        txtButtonConfirm: 'Sim',
        handleConfirm() {
          toggleEditEffectiveModal()
        },
        txtButtonCancel: 'Cancelar'
      })
    } catch (error: any) {
      setLoading('idle')
      showModal({
        hideButtonCancel: true,
        message:
          error.mensagem ||
          error.message ||
          'Não foi possivel obter dados do Efetivo, tente novamente.',
        txtButtonConfirm: 'Fechar',
        type: 'error'
      })
    }
  }

  const revalidateCollaborator = async (idEfetivoLogico: number) => {
    setLoading('loading')

    try {
      const response = await EfetivoLogicoServices().revalidaEfetivoLogico(
        idEfetivoLogico
      )

      if (response.status !== 200) {
        throw new Error(
          'Não foi possível revalidar colaborador, tente novamente.'
        )
      }

      setLoading('idle')
      showModal({
        hideButtonCancel: true,
        message: 'Colaborador foi revalidado com sucesso!',
        txtButtonConfirm: 'Fechar',
        type: 'success'
      })

      getEfetivosPorContrato()
    } catch (error: any) {
      setLoading('idle')
      showModal({
        hideButtonCancel: true,
        message:
          error.mensagem ||
          error.message ||
          'Não foi possivel revalidar colaborador, tente novamente.',
        txtButtonConfirm: 'Fechar',
        type: 'error'
      })
    }
  }

  const handleClickRevalidate = (
    idEfetivoLogico: number,
    nmColaborador: string
  ) => {
    showModal({
      handleConfirm: () => {
        revalidateCollaborator(idEfetivoLogico)
      },
      message: `Revalidar acesso do colaborador ${nmColaborador} ?`,
      txtButtonConfirm: 'Revalidar',
      type: 'alert'
    })
  }

  const changeStatusCollaborator: (
    params: ReqUpdateStatusEffective
  ) => Promise<void> = async (params: ReqUpdateStatusEffective) => {
    const actionDone: 'liberado' | 'bloqueado' | 'demitido' =
      params.status === 'L' ? 'liberado' : params.status === 'D' ? 'demitido' : 'bloqueado'
    setLoading('loading')

    try {
      const reqUpdateStatusEffectiveObj: ReqUpdateStatusEffective = {
        cdContrato: params.cdContrato,
        flEnviaScarf: params.flEnviaScarf,
        flReplicarStatus: params.flReplicarStatus,
        cdCpf: params.cdCpf,
        idColaboradorEfetivoLogico: params.idColaboradorEfetivoLogico,
        idContrato: contractData.idContrato || 0,
        status: params.status
      }
      const response: AxiosResponse<updateEffectiveResponse> =
        await EfetivoLogicoServices().atualizaStatusColaborador(
          reqUpdateStatusEffectiveObj
        )

      if (response.status !== 200) {
        throw new Error(`Colaborador não foi ${actionDone}, verifique!`)
      }

      setLoading('idle')
      showModal({
        hideButtonCancel: true,
        message: `Colaborador ${actionDone} com sucesso!`,
        txtButtonConfirm: 'Fechar',
        type: 'success'
      })

      params.flReplicarStatus &&
        handleChangeTab({
          contractData: contractData,
          newValue: 0,
          resetTab: true
        })
      getEfetivosPorContrato()
    } catch (error: any) {
      setLoading('idle')
      showModal({
        hideButtonCancel: true,
        message: `${error.response.data.title}. Não ${actionDone}, verifique!`,
        txtButtonConfirm: 'Fechar',
        type: 'error'
      })
    }
  }

  const handleClickBlock = (
    idEfetivoLogico: number,
    nmColaborador: string,
    maisDeUmContrato: boolean,
    cdCpf: string
  ) => {
    const updateStatusToBlockEffectiveObj: ReqUpdateStatusEffective = {
      flEnviaScarf: false,
      idColaboradorEfetivoLogico: idEfetivoLogico,
      cdCpf: cdCpf,
      cdContrato: selectedContract,
      status: 'D'
    }

    const showSecondModal = (updateStatusToBlockEffectiveObj: ReqUpdateStatusEffective) => {
      showModal({
        hideThirdButton: true,
        message: `Você tem certeza que deseja demitir esse colaborador? Esse processo não poderá ser revertido.`,
        handleConfirm: () => {
          const newUpdateStatusObject: ReqUpdateStatusEffective = {
            ...updateStatusToBlockEffectiveObj,
            flReplicarStatus: false
          }
          changeStatusCollaborator(newUpdateStatusObject)
        },
        handleCancel: () => {
          setTimeout(() => handleBack(updateStatusToBlockEffectiveObj), 0);
        },
        txtButtonCancel: 'Não',
        typeButtonCancel: 'outlined',
        txtButtonConfirm: 'Sim',
        type: 'alert'
      });
    };

    const handleBack = (updateStatusToBlockEffectiveObj: ReqUpdateStatusEffective) => {
      showModal({
        hideThirdButton: false,
        message: `Você deseja bloquear ou demitir este colaborador?`,
        handleConfirm: () => {
          setTimeout(() => showSecondModal(updateStatusToBlockEffectiveObj), 0);
        },
        handleCancel: () => {
          const newUpdateStatusObject: ReqUpdateStatusEffective = {
            ...updateStatusToBlockEffectiveObj,
            flReplicarStatus: false,
            status: 'B'
          }
          changeStatusCollaborator(newUpdateStatusObject)
        },
        txtButtonCancel: 'Bloquear',
        typeButtonCancel: 'outlined',
        txtButtonConfirm: 'Demitir',
        type: 'alert'
      });
    };

    showModal({
      hideThirdButton: false,
      message: `Você deseja bloquear ou demitir este colaborador?`,
      handleConfirm: () => {
        setTimeout(() => showSecondModal(updateStatusToBlockEffectiveObj), 0);
      },
      handleCancel: () => {
        const newUpdateStatusObject: ReqUpdateStatusEffective = {
          ...updateStatusToBlockEffectiveObj,
          flReplicarStatus: false,
          status: 'B'
        }
        changeStatusCollaborator(newUpdateStatusObject)
      },
      txtButtonCancel: 'Bloquear',
      typeButtonCancel: 'outlined',
      txtButtonConfirm: 'Demitir',
      type: 'alert'
    });
    // showModal({
    //   handleConfirm: () => {
    //     const updateStatusToBlockEffectiveObj: ReqUpdateStatusEffective = {
    //       flEnviaScarf: false,
    //       idColaboradorEfetivoLogico: idEfetivoLogico,
    //       cdCpf: cdCpf,
    //       cdContrato: selectedContract,
    //       status: 'B'
    //     }

    //     // Verifica se idColaborador consta em mais de um contrato
    //     if (maisDeUmContrato) {
    //       setUpdateStatusObject(updateStatusToBlockEffectiveObj)
    //       setBloquear(true)
    //     } else {
    //       changeStatusCollaborator(updateStatusToBlockEffectiveObj)
    //     }
    //   },
    //   message: `Bloquear colaborador ${nmColaborador} ?`,
    //   txtButtonConfirm: 'Bloquear',
    //   type: 'alert'
    // })
  }

  const handleClickUnlock = (
    idEfetivoLogico: number,
    nmColaborador: string,
    maisDeUmContrato: boolean,
    cdCpf: string
  ) => {
    showModal({
      handleConfirm: () => {
        const updateStatusToReleaseEffectiveObj: ReqUpdateStatusEffective = {
          flEnviaScarf: false,
          idColaboradorEfetivoLogico: idEfetivoLogico,
          cdCpf: cdCpf,
          cdContrato: selectedContract,
          status: 'L'
        }
        // // Verifica se idColaborador consta em mais de um contrato
        // if (maisDeUmContrato) {
        //   setUpdateStatusObject(updateStatusToReleaseEffectiveObj)
        //   setDesbloquear(true)
        // } else {
        //   changeStatusCollaborator(updateStatusToReleaseEffectiveObj)
        // }
        changeStatusCollaborator(updateStatusToReleaseEffectiveObj)
      },
      message: `Você deseja desbloquear o colaborador ${nmColaborador}?`,
      txtButtonConfirm: 'Desbloquear',
      type: 'alert'
    })
  }

  const updateEffectiveData = async (effectiveData: EffectiveData) => {
    setLoading('loading')

    const requestQuery: EffectiveData = {
      cdCep: FORMAT.unMask(effectiveData.cdCep),
      cdCnh: FORMAT.unMask(effectiveData.cdCnh),
      cdCpf: FORMAT.unMask(effectiveData.cdCpf),
      cdCtps: FORMAT.unMask(effectiveData.cdCtps),
      cdDocumento: FORMAT.unMask(effectiveData.cdDocumento),
      cdPis: FORMAT.unMask(effectiveData.cdPis),
      cdSerieCtps: effectiveData.cdSerieCtps,
      dtNascimento: new Date(effectiveData.dtNascimento),
      flUsaRefeitorio: effectiveData.usaRefeitorio,
      idContrato: contractData.idContrato,
      idEfetivoLogico: effectiveData.idEfetivoLogico,
      nmColaborador: effectiveData.nmColaborador,
      nuNumero: effectiveData.nuNumero,
      sgEstadoCtps: effectiveData.sgEstadoCtps,
      sgEstadoDocto: effectiveData.sgEstadoDocto,
      sgEstadoEnd: effectiveData.sgEstadoEnd,
      sgGenero: effectiveData.sgGenero,
      txBairro: effectiveData.txBairro,
      txCidade: effectiveData.txCidade,
      txComplemento: effectiveData.txComplemento,
      txEmissorDocto: effectiveData.txEmissorDocto,
      txEndereco: effectiveData.txEndereco,
      txFiliacaoMae: effectiveData.txFiliacaoMae,
      txFiliacaoPai: effectiveData.txFiliacaoPai,
      txFuncao: effectiveData.txFuncao,
      txLocalAcesso: effectiveData.txLocalAcesso,
      txMatricula: effectiveData.txMatricula,
      txNacionalidade: effectiveData.txNacionalidade,
      txNaturalidade: effectiveData.txNaturalidade
    }

    try {
      const response = await EfetivoLogicoServices().updateEfetivoLogico(
        requestQuery
      )

      if (response.status !== 200) {
        throw new Error('Erro ao salvar dados do Colaborador, tente novamente')
      }

      showModal({
        handleConfirm: () => {
          toggleEditEffectiveModal()
          handleChangeTab({
            contractData: contractData,
            newValue: 0,
            resetTab: true
          })
        },
        hideButtonCancel: true,
        message: 'Colaborador atualizado com sucesso!',
        txtButtonConfirm: 'Fechar',
        type: 'success'
      })

      setLoading('idle')
    } catch {
      showModal({
        hideButtonCancel: true,
        message: 'Erro ao salvar dados do Colaborador, tente novamente',
        txtButtonConfirm: 'Fechar',
        type: 'error'
      })
      setLoading('idle')
    }
  }

  const handleClickAddEffective = () => {
    handleChangeTab({
      contractData: contractData,
      newValue: 1
    })
  }

  const handleExcel = async () => {
    if (effectiveList.length > 0) {
      setLoading('loading')

      try {
        const response = await EfetivoLogicoServices().getExcelEfetivoLogico({
          cdContrato: selectedContract,
          idUsuario: user.IdUsuario
        })

        if (response.status !== 200) {
          throw new Error('Erro ao baixar arquivo Excel, tente novamente.')
        }

        const url =
          'data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,' +
          response.data
        const downloadLink = document.createElement('a')
        downloadLink.href = url
        downloadLink.download = `efetivo-logico-contrato-${selectedContract}.xlsx`
        document.body.appendChild(downloadLink)
        downloadLink.click()
        document.body.removeChild(downloadLink)

        setLoading('idle')
      } catch {
        showModal({
          hideButtonCancel: true,
          message: 'Erro ao baixar arquivo Excel, tente novamente.',
          txtButtonConfirm: 'Fechar',
          type: 'error'
        })
        setLoading('idle')
      }
    } else {
      showModal({
        hideButtonCancel: true,
        message: 'Não há efetivo cadastrado para este contrato.',
        txtButtonConfirm: 'Fechar',
        type: 'alert'
      })
    }
  }

  const getStatusColor = (cdStatus: string) => {
    const statusName = { B: 'Bloqueado', L: 'Liberado', P: 'Pendente' }
    return {
      content: statusName[cdStatus] || 'Pendente',
      styles: {
        fillColor: colorsStatus[cdStatus],
        fontStyle: 'bold'
      }
    }
  }

  const handleClickPdfReport = async () => {
    const requestForReport: ReqEffectiveByContract = {
      cdContrato: selectedContract,
      flObterTodos: true,
      idUsuario: user.IdUsuario,
      pageNumber: 0,
      pageSize: 0
    }

    setLoading('loading')

    try {
      const response = await EfetivoLogicoServices().getEfetivosporContrato(
        requestForReport
      )

      if (response.status !== 200) {
        throw new Error(
          'Página em construção, não há necessidade de lançamentos.'
        )
      }

      setLoading('idle')

      if (response.data.items.length > 0) {
        handlePrintEfetivoLogico({
          filename: `relatorio-efetivo-logico-contrato-${selectedContract}`,
          data: {
            titleCollapse: contractData.objetoContrato,
            cdContrato: selectedContract,
            empresa: selectedNmEmpresa,
            dataIni: contractData.dtInicioVigencia,
            dataFim: contractData.dtFimVigencia,
            body: response.data.items?.map((efetivo) => [
              efetivo.nmColaborador,
              FORMAT.documento(efetivo.cdCpf),
              FORMAT.formatDDMMYYYY(efetivo.dtNascimento),
              efetivo.txFuncao,
              FORMAT.formatDDMMYYYY(efetivo.dtUltAtualizacao),
              efetivo.qtDias,
              getStatusColor(efetivo.statusBloqueio)
            ])
          }
        })
      } else {
        showModal({
          hideButtonCancel: true,
          message: 'Não há efetivo cadastrado para este contrato.',
          txtButtonConfirm: 'Fechar',
          type: 'alert'
        })
      }
    } catch (error: any) {
      setLoading('idle')
      showModal({
        hideButtonCancel: true,
        message:
          error.mensagem ||
          error.message ||
          'Página em construção, não há necessidade de lançamentos.',
        txtButtonConfirm: 'Fechar',
        type: 'error'
      })
    }
  }

  const handleDeleteEfetivoContrato = (idEfetivoLogicoContrato: number) => {
    showModal({
      type: 'alert',
      titleModal: 'Atenção!',
      message: 'Tem certeza de que deseja deletar este efetivo lógico?',
      txtButtonConfirm: 'SIM',
      txtButtonCancel: 'Cancelar',
      handleConfirm: () => {
        setLoading('loading')

        EfetivoLogicoServices()
          .deleteEfetivoLogicoByContrato(idEfetivoLogicoContrato)
          .then(({ status }) => {
            if (status === 200) {
              getEfetivosPorContrato()

              showModal({
                type: 'success',
                message: 'Efetivo lógico excluido com sucesso',
                txtButtonConfirm: 'OK',
                hideButtonCancel: true
              })
            }
          })
          .catch(() => {
            setLoading('idle')
            showModal({
              type: 'error',
              message: 'Não foi possivel atualizar o efetivo lógico',
              txtButtonConfirm: 'OK',
              hideButtonCancel: true
            })
          })
          .finally(() => setLoading('idle'))
      }
    })
  }

  useEffect(() => {
    getEfetivosPorContrato()
  }, [pageNumber, rowsPerPage])

  return (
    <>
      <Row
        justifyContent="flex-end"
        marginTop={SPACING.spacing10}
        marginBottom={SPACING.spacing10}
      >
        <Button
          text="ADICIONAR NOVO"
          onClick={() => handleClickAddEffective()}
        />
      </Row>
      {effectiveList.length === 0 ? (
        <Row
          alignItems="center"
          justifyContent="center"
          marginBottom={SPACING.spacing30}
          marginTop={SPACING.spacing20}
        >
          <Span
            text="Não há colaborador cadastrado para este contrato."
            fontSize="1rem"
            fontWeight="bold"
            color={COLORS.info}
          />
        </Row>
      ) : (
        <TableRegisterEffectives
          handleDeleteEfetivoContrato={handleDeleteEfetivoContrato}
          effectiveList={effectiveList}
          setPage={setPageNumber}
          pageCount={pageCount}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          page={pageNumber}
          handleClickRevalidate={handleClickRevalidate}
          handleClickBlock={handleClickBlock}
          handleClickUnlock={handleClickUnlock}
          handleEditEffective={getEffectiveData}
        />
      )}
      <Row
        justifyContent="flex-start"
        marginTop={SPACING.spacing10}
        marginBottom={SPACING.spacing10}
      >
        <ExcelIcon
          onClick={() => handleExcel()}
          disabled={effectiveList.length === 0}
        />
        <PdfIcon
          onClick={() => handleClickPdfReport()}
          disabled={effectiveList.length === 0}
        />
      </Row>
      <EditEffectiveModal
        showModal={openEditEffectiveModal}
        toggleModal={toggleEditEffectiveModal}
        effectiveData={selectedEffective}
        handleSubmitUpdate={updateEffectiveData}
        contractData={contractData}
      />
    </>
  )
}
